import React, { useRef, useState } from "react";
import { Button, Form, Icon, Loader, Message, Select } from "semantic-ui-react";
import { DatesRangeInput } from 'semantic-ui-calendar-react';

import { useAuth } from "hooks";
import { fhirGet, foodCourtPut, getDeliveryUploadUrl, multipleFhirGet } from "services";
import { DEFAULT_ERROR_MESSAGE } from "utils";

import OrdersTable from "./table";
import useSWR from "swr";
import moment from "moment";
import ExportOrders from "./export";
import * as FHIRUtils from "../../../../utils/FHIRUtils";

type PatientOrdersProps = {
  patientId: string;
};

const PatientOrders: React.FC<PatientOrdersProps> = ({ patientId }) => {
  const PATIENT_ATTRIBUTES = [
    'firstName',
    'lastName',
    'delivery_addressLine1',
    'delivery_addressLine2',
    'delivery_city',
    'delivery_state',
    'delivery_zip',
    'email',
    'homePhone',
    'mobilePhone',
  ];

  const { token, personName, organizationId } = useAuth();
  let [loading, setLoading] = useState(true);
  let [statusvalue, setStatusValue] = useState('');
  let [dateValue, setDateValue] = useState(null);
  let [searchId, setSearchId] = useState('');
  let [statusOptions, setStatusOptions] = useState(null);
  let [dateState, setdateState] = useState('');
  let [expectedDateState, setExpectedDateState] = useState('');
  let [startRange, setStartRange] = useState('');
  let [endRange, setEndRange] = useState('');
  let [startExpectedDateRange, setStartExpectedDateRange] = useState('');
  let [endExpectedDateRange, setEndExpectedDateRange] = useState('');
  let [disableCreatedDateFilter, setDisableCreatedDateFilter] = useState(false);
  let [disableExpectedDueDateFilter, setDisableExpectedDueDateFilter] = useState(false);
  const [rawPatients, setRawPatients] = useState(null);
  const [patients, setPatients] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getPatients = (urls) => {
    multipleFhirGet(token, ...urls).then(
      (patientsDataset) => {
        let combinedPatients = [];
        let combinedRawPatients = [];
        patientsDataset?.forEach((patientsData) => {
          if (patientsData && patientsData.entry && patientsData.entry.length) {
            let tempPatients = {};
            let tempRawPatients = {};
            patientsData.entry.forEach((item) => {
              tempPatients[item.resource.id] = FHIRUtils.parseFHIRPatient(item.resource);
              tempRawPatients[item.resource.id] = item.resource;
            });
            combinedPatients = { ...combinedPatients, ...tempPatients };
            combinedRawPatients = {
              ...combinedRawPatients,
              ...tempRawPatients,
            };
            setLoading(false);
          }
        });
        setPatients(combinedPatients);
        setRawPatients(combinedRawPatients);
      },
      (error) => { }
    );
  };

  let url = '';
  if (patientId) {
    url = `?patient=` + patientId;
  } else {
    if (statusvalue) {
      url = '?status=' + statusvalue;
    } else {
      url = '?status:ne=draft';
    }
    url +=
      (searchId ? '&orderId=' + searchId : '') +
      (startRange && !startExpectedDateRange
        ? '&createdDate:ge=' +
        moment(startRange).utc().format('YYYY-MM-DD') +
        (endRange ? '&createdDate:le=' + moment(endRange).utc().add(1, 'days').format('YYYY-MM-DD') : '')
        : '') +
      (!startRange && startExpectedDateRange
        ? '&estimatedDeliveryDate:ge=' +
        moment(startExpectedDateRange).utc().format('YYYY-MM-DD') +
        (endExpectedDateRange
          ? '&estimatedDeliveryDate:le=' + moment(endExpectedDateRange).utc().add(1, 'days').format('YYYY-MM-DD')
          : '')
        : '')
  }

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onError: (error) => {
      setLoading(false);
      console.error("Error occurred while fetching data:", error);
    },
    onSuccess: (data) => {
      setLoading(false);
      if (data && data.length > 0) {
        setStatusOptions(
          Array.from(
            new Set(
              data &&
              data.map((order) => {
                return order.status;
              })
            )
          )
        );
        let patientsObj = data.map((order) => {
          return { id: order.patientId, org: order.patientOrg };
        });
        let orgPatients = {};
        patientsObj?.forEach((patient) => {
          if (patient.org) {
            if (orgPatients.hasOwnProperty(patient.org)) {
              orgPatients[patient.org].push(patient.id);
            } else {
              orgPatients[patient.org] = [patient.id];
            }
          }
        });
        let urls = [];
        if (Object.keys(orgPatients).length) {
          Object.keys(orgPatients).forEach((org) => {
            let patients = orgPatients[org];
            let uniquePatients = patients.filter((c, index) => {
              if (c && c !== '') return patients.indexOf(c) === index;
            });
            if (uniquePatients && uniquePatients.length) {
              const groups = uniquePatients
                .map((e, i) => {
                  return i % 50 === 0 ? uniquePatients.slice(i, i + 50) : null;
                })
                .filter((e) => {
                  return e;
                }); // creating chunks to fetch lesser patientIds in each call
              for (var groupIndex in groups) {
                urls.push(`/Patient/?organization=${org}&_id=${groups[groupIndex].join(',')}`);
              }
            }
          });
          getPatients(urls);
        } else {
          setLoading(false);
        }
      }
    }
  };

  const { data: ordersData, error: ordersError, mutate: ordersMutate } = useSWR(
    [organizationId, `/NutritionOrder/${url}`, token],
    (_orgId, path, authToken) => fhirGet(path, authToken),
    options
  );

  const resetFilters = () => {
    setStatusValue(null);
    setDateValue(null);
    setdateState('');
    setStartRange('');
    setEndRange('');
    setSearchId('');
    setEndExpectedDateRange('');
    setStartExpectedDateRange('');
    setExpectedDateState('');
    //dispatch({ type: 'CLEAR_FILTERS', data: ordersList });
    setDisableCreatedDateFilter(false);
    setDisableExpectedDueDateFilter(false);
  };

  const handleSearchById = (e, searchProps) => {
    setSearchId(searchProps.value);
  };

  const handleChangeDate = (event, { name, value }) => {
    setdateState(value);
    let temp = value.split(' - ');
    setStartRange(temp[0]);
    setEndRange(temp[1]);
    setStartExpectedDateRange('');
    setEndExpectedDateRange('');
    setExpectedDateState('');
    setDisableExpectedDueDateFilter(true);
  };

  //for estimated delivery date
  const handleChangeExpectedDate = (event, { name, value }) => {
    setExpectedDateState(value);
    let temp = value.split(' - ');
    setStartExpectedDateRange(temp[0]);
    setEndExpectedDateRange(temp[1]);
    setStartRange('');
    setEndRange('');
    setdateState('');
    setDisableCreatedDateFilter(true);
  };

  async function markExported() {
    setLoading(true);
    let temp = ordersData
    for (let i = 0; i < temp.length; i++) {
      let order = temp[i];
      if (order.status && (order.status.toLowerCase() === 'new' || order.status.toLowerCase() === 'requested')) {
        let orderUpdate = {
          patientId: order.patientId,
          docId: order.orderId,
          status: 'exported',
          exportedBy: personName,
          exportFile: 'EW' + '-' + moment().utc().format(),
          exportedDate: moment().utc().format(),
          lastUpdated: moment().utc().format(),
          lastUpdatedBy: personName,
        };
        await foodCourtPut(`/order/` + order.orderId.toString(), token, orderUpdate).then(() => { });
      }
    }
    //setOrdersMessage('Order Exported Successfully!');
    ordersMutate();
    setLoading(false);
  }

  const handleUploadClick = () => {
    setUploadError(null);
    setUploadSuccess(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setUploadError("Please select a CSV file.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    try {
      const res = await getDeliveryUploadUrl(token, file.name);
      const body = res?.body;
      const parsed =
        typeof body === "string" ? (JSON.parse(body) as { uploadUrl?: string }) : body;
      const presignedUrl = parsed?.uploadUrl ?? res?.uploadUrl;
      if (!presignedUrl) {
        throw new Error("No upload URL returned from server");
      }
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        // Backend (payer-services upload-url) signs with ContentType: text/csv.
        // The client must send this exact header or S3 returns 403.
        headers: { "Content-Type": "text/csv" },
      });
      if (!uploadResponse.ok) {
        const errBody = await uploadResponse.text();
        const detail =
          errBody && errBody.length < 200 ? errBody : `${uploadResponse.status} ${uploadResponse.statusText}`;
        throw new Error(`Upload failed: ${detail}`);
      }
      setUploadSuccess(true);
      ordersMutate();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div>
        <>
          {!patientId && (
            <>
              {uploadError && (
                <Message negative onDismiss={() => setUploadError(null)}>
                  {uploadError}
                </Message>
              )}
              {uploadSuccess && (
                <Message success onDismiss={() => setUploadSuccess(false)}>
                  CSV uploaded successfully.
                </Message>
              )}
              <Form autoComplete="off">
                {/* One row when space allows: [Search|Date|Expected|Status|Clear] --- [Upload|Download]. When narrow: right wraps to row 2 and/or left scrolls. */}
                <div
                  className="orders-filters-row"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0,
                    rowGap: '0.5rem',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {/* Left cluster: no whitespace between items. flex 0 1 auto so it doesn't grow — whitespace stays between Clear and Upload. */}
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      gap: 0,
                      flex: '0 1 auto',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        gap: 0,
                        minWidth: 0,
                        overflowX: 'auto',
                        flex: '1 1 0',
                      }}
                    >
                      <Form.Input
                        placeholder="Search by ID"
                        icon={<Icon name="search" onClick={handleSearchById} />}
                        value={searchId}
                        onChange={(e, searchProps) => setSearchId(searchProps.value)}
                        name="searchId"
                        style={{ flex: '0 0 auto', width: '140px', minWidth: '140px', margin: 0 }}
                      />
                      <DatesRangeInput
                        name="datesRange"
                        placeholder="Filter by Date Range"
                        value={dateState}
                        iconPosition="left"
                        dateFormat="YYYY-MM-DD"
                        onChange={handleChangeDate}
                        className={disableCreatedDateFilter ? 'disabled field' : ''}
                        style={{ flex: '0 0 auto', width: '200px', minWidth: '200px', margin: 0 }}
                      />
                      <DatesRangeInput
                        name="datesRange"
                        placeholder="Filter by Expected Date Range"
                        value={expectedDateState}
                        iconPosition="left"
                        dateFormat="YYYY-MM-DD"
                        onChange={handleChangeExpectedDate}
                        className={disableExpectedDueDateFilter ? 'disabled field' : ''}
                        style={{ flex: '0 0 auto', width: '260px', minWidth: '260px', margin: 0 }}
                      />
                    </div>
                    <Form.Field
                      control={Select}
                      search
                      name="status"
                      selectOnBlur={false}
                      scrolling
                      button
                      basic
                      placeholder="Filter by Status"
                      value={statusvalue}
                      options={
                        statusvalue
                          ? [{ key: statusvalue, text: statusvalue.toString().toUpperCase(), value: statusvalue }]
                          : [
                            { key: 'draft', text: 'DRAFT', value: 'draft' },
                            ...(statusOptions || [])
                              .filter((x) => x !== 'draft')
                              .map((x) => ({
                                key: x,
                                text: x.toString().toUpperCase(),
                                value: x,
                              })),
                          ]
                      }
                      onChange={(e, statusprops) => setStatusValue(statusprops.value)}
                      style={{ flex: '0 0 auto', width: '160px', minWidth: '160px', margin: 0, flexShrink: 0 }}
                    />
                    <Form.Button content="Clear" color="blue" onClick={resetFilters} style={{ flexShrink: 0, margin: 0 }} />
                  </div>
                  {/* Right cluster: no whitespace between buttons; pushed right; wraps to row 2 when not enough space */}
                  <div
                    className="orders-filters-actions"
                    style={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      gap: 0,
                      flexShrink: 0,
                      marginLeft: 'auto',
                      paddingLeft: 0,
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    <Button
                      color="blue"
                      icon="upload"
                      content="Upload"
                      onClick={handleUploadClick}
                      disabled={uploading}
                      loading={uploading}
                    />
                    <ExportOrders
                      vendor={'EW'}
                      markExported={markExported}
                      patients={patients}
                      PATIENT_ATTRIBUTES={PATIENT_ATTRIBUTES}
                      patientId={patientId}
                      ordersData={ordersData}
                    />
                  </div>
                </div>
              </Form>
          </>
          )}

        {((!ordersData && !ordersError) || loading) && (
          <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader active inline="centered" />
          </div>
        )}
        {ordersData && ordersData.length === 0 && !loading && (
          <Message warning header="Info" content="0 items found!" />
        )}
        {ordersError && !loading && (
          <Message header={DEFAULT_ERROR_MESSAGE} content={ordersError.message} negative />
        )}
        {ordersData && ordersData.length > 0 && !loading && (
          <OrdersTable data={ordersData} vendorView={!patientId} />
        )}
        </>
      </div>
    </div>
  );
};
export default PatientOrders;
